import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Boxes, Edit, Plus, Trash2 } from 'lucide-react'

import type {
    ListPackagingMaterialsParams,
    PackagingMaterial,
} from '@/lib/api/packaging-api'
import { BOMEditDialog } from '@/components/packaging/bom-edit-dialog'
import { MaterialFormDialog } from '@/components/packaging/material-form-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/use-auth'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useBOMCounts,
    useDeletePackagingMaterial,
    usePackagingMaterials,
} from '@/hooks/use-packaging-query'
import { useProducts } from '@/hooks/use-product-query'
import { m } from '@/lib/paraglide/messages'

const PERMISSION = 'packaging.read'
const BOM_PER_PAGE = 50

export const Route = createFileRoute('/_app/_authed/_sidebar/packaging/')({
    component: PackagingPage,
})

function PackagingPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    if (arePermissionsLoaded && !hasPermission(PERMISSION)) {
        return <ForbiddenState />
    }
    return <PackagingContent />
}

function PackagingContent() {
    return (
        <div className="space-y-4 p-4 md:p-6">
            <div className="flex items-center gap-2">
                <Boxes className="size-6 text-primary" />
                <div>
                    <h1 className="text-2xl font-semibold">
                        {m.packaging_page_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.packaging_page_subtitle()}
                    </p>
                </div>
            </div>

            <Tabs defaultValue="materials" className="space-y-4">
                <TabsList className="w-fit self-center">
                    <TabsTrigger value="materials">
                        {m.packaging_tab_materials()}
                    </TabsTrigger>
                    <TabsTrigger value="bom">
                        {m.packaging_tab_bom()}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="materials">
                    <MaterialsTab />
                </TabsContent>
                <TabsContent value="bom">
                    <BOMTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}

function MaterialsTab() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('packaging.write')
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)
    const [page, setPage] = useState(1)
    const [createOpen, setCreateOpen] = useState(false)
    const [editing, setEditing] = useState<PackagingMaterial | null>(null)
    const deleteMutation = useDeletePackagingMaterial()

    const params = useMemo<ListPackagingMaterialsParams>(
        () => ({
            page,
            per_page: 50,
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
        }),
        [page, debouncedSearch],
    )
    const { data, isLoading } = usePackagingMaterials(params)
    const items = data?.items ?? []
    const total = data?.meta?.total ?? 0
    const perPage = data?.meta?.per_page ?? 50
    const currentPage = data?.meta?.page ?? page

    const handleDelete = (mat: PackagingMaterial) => {
        if (
            !window.confirm(
                m.packaging_material_delete_confirm({ name: mat.name }),
            )
        )
            return
        deleteMutation.mutate(mat.id)
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex-1 max-w-md">
                    <Input
                        placeholder={m.packaging_search_placeholder()}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            setPage(1)
                        }}
                    />
                </div>
                {canWrite ? (
                    <Button onClick={() => setCreateOpen(true)}>
                        <Plus className="size-4 mr-1" />
                        {m.packaging_material_create()}
                    </Button>
                ) : null}
            </div>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.packaging_material_sku()}</TableHead>
                            <TableHead>{m.packaging_material_name()}</TableHead>
                            <TableHead>{m.packaging_material_unit()}</TableHead>
                            <TableHead>
                                {m.packaging_material_tracking_mode_col()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.packaging_material_min_stock()}
                            </TableHead>
                            <TableHead>
                                {m.packaging_material_barcode()}
                            </TableHead>
                            <TableHead>
                                {m.packaging_material_active()}
                            </TableHead>
                            {canWrite ? (
                                <TableHead className="text-right">
                                    {m.packaging_bom_actions()}
                                </TableHead>
                            ) : null}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={canWrite ? 8 : 7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={canWrite ? 8 : 7}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.packaging_material_table_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map((mat) => (
                                <TableRow key={mat.id}>
                                    <TableCell className="font-mono text-sm">
                                        {mat.sku}
                                    </TableCell>
                                    <TableCell className="whitespace-normal">
                                        {mat.name}
                                    </TableCell>
                                    <TableCell>{mat.unit}</TableCell>
                                    <TableCell>
                                        {mat.tracking_mode === 'quantity' ? (
                                            <Badge
                                                variant="outline"
                                                className="border-blue-300 text-blue-700 bg-blue-50"
                                            >
                                                {m.packaging_material_tracking_mode_quantity()}
                                            </Badge>
                                        ) : (
                                            <Badge
                                                variant="outline"
                                                className="border-slate-300 text-slate-700"
                                            >
                                                {m.packaging_material_tracking_mode_lot()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {mat.min_stock}
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                        {mat.barcode ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {mat.is_active ? (
                                            <Badge className="bg-green-100 text-green-700">
                                                ✓
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline">
                                                {m.packaging_material_status_inactive()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    {canWrite ? (
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() =>
                                                        setEditing(mat)
                                                    }
                                                >
                                                    <Edit className="size-4" />
                                                </Button>
                                                {mat.is_active ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() =>
                                                            handleDelete(mat)
                                                        }
                                                        disabled={
                                                            deleteMutation.isPending
                                                        }
                                                    >
                                                        <Trash2 className="size-4 text-red-500" />
                                                    </Button>
                                                ) : null}
                                            </div>
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {currentPage}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || currentPage * perPage >= total}
                    onClick={() => setPage((p) => p + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            <MaterialFormDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                material={null}
            />
            <MaterialFormDialog
                open={Boolean(editing)}
                onOpenChange={(v) => !v && setEditing(null)}
                material={editing}
            />
        </div>
    )
}

function BOMTab() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('packaging.write')
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)
    const [page, setPage] = useState(1)
    const [editing, setEditing] = useState<{
        id: string
        name: string
    } | null>(null)

    const { data: productsData, isLoading } = useProducts({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        page,
        per_page: BOM_PER_PAGE,
    })
    const products = useMemo(() => productsData?.data ?? [], [productsData])
    const total = productsData?.meta?.total ?? 0
    const currentPage = productsData?.meta?.page ?? page
    const productIds = useMemo(() => products.map((p) => p.id), [products])
    const { data: counts = {} } = useBOMCounts(productIds)

    return (
        <div className="space-y-4">
            <div className="max-w-md">
                <Input
                    placeholder={m.packaging_search_placeholder()}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value)
                        setPage(1)
                    }}
                />
            </div>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.packaging_bom_sku()}</TableHead>
                            <TableHead>{m.packaging_bom_product()}</TableHead>
                            <TableHead className="text-right">
                                {m.packaging_bom_count()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.packaging_bom_actions()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : products.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.packaging_material_table_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            products.map((p) => {
                                const summary = counts[p.id]
                                return (
                                    <TableRow key={p.id}>
                                        <TableCell className="font-mono text-sm">
                                            {p.sku}
                                        </TableCell>
                                        <TableCell className="whitespace-normal">
                                            {p.name}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {summary?.count ?? 0}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    setEditing({
                                                        id: p.id,
                                                        name: p.name,
                                                    })
                                                }
                                                disabled={!canWrite}
                                            >
                                                <Edit className="size-4 mr-1" />
                                                {m.packaging_bom_edit()}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {currentPage}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || currentPage * BOM_PER_PAGE >= total}
                    onClick={() => setPage((p) => p + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            {editing ? (
                <BOMEditDialog
                    open={Boolean(editing)}
                    onOpenChange={(v) => !v && setEditing(null)}
                    productId={editing.id}
                    productName={editing.name}
                />
            ) : null}
        </div>
    )
}

function ForbiddenState() {
    return (
        <Card>
            <CardContent className="py-12 text-center space-y-2">
                <h2 className="text-lg font-semibold">
                    {m.packaging_forbidden_title()}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {m.packaging_forbidden_desc()}
                </p>
            </CardContent>
        </Card>
    )
}
